import { Column } from "react-table";
import moment from "moment";
import { FormattedMessage, IntlShape } from "react-intl";
import { convertWeight, formatWeight } from "helpers/commonHelpers";
import { IGoodItem } from "pages/provider/QuotationDetail/QuotationType";
import { UnitSystemPreferences } from "types/Common";
import "./SelectedOrders.scss";

function getSelectionOrderColumns(
  intl: IntlShape,
  isShipmentReview: boolean | undefined,
  handleQRClick: any,
  unit_system_preferences: UnitSystemPreferences,
) {
  function formatDateRange(startDate: string, endDate?: string): string {
    if (startDate && endDate) {
      // Format and display the range: DD MMM - DD MMM YYYY
      const formattedStartDate = moment(startDate).format("DD MMM");
      const formattedEndDate = moment(endDate).format("DD MMM YYYY");
      return `${formattedStartDate} - ${formattedEndDate}`;
    }
    if (startDate) {
      // If end_date is not available
      return moment(startDate).format("DD MMM YYYY");
    }
    return "";
  }

  const columns: Column<any>[] = [
    {
      Header: "Order #",
      accessor: (row) => [row?.data?.organization?.quick_request_reference, row?.data?.organization?.quick_request_title],
      minWidth: isShipmentReview ? 410 : 141,
      Cell: ({ value }: any) => (
        <div className="d-flex align-items-center">
          <div className="d-flex flex-column">
            <span onClick={(e) => handleQRClick(e, value[0])} style={{ color: "#0071CE", cursor: "pointer" }}>
              {value[0]}
            </span>
            <span
              className={`text-nowrap selected-order-desc ${isShipmentReview ? "review" : ""}`}
              style={{ color: "#64748B" }}
            >
              {value[1]}
            </span>
          </div>
        </div>
      ),
    },
    // Add "Pickup Date" and "Delivery Date" columns if `isShipmentReview` is false
    ...(!isShipmentReview
      ? [
          {
            Header: intl.formatMessage({ id: "general.pickupDate", defaultMessage: "Pickup Date" }),
            accessor: (row: any) => [row?.data?.schedule?.pickup_date, row?.data.schedule?.pickup_end_date],
            minWidth: 187,
            Cell: ({ value }: any) => <div>{formatDateRange(value[0], value[1])}</div>,
          },
          {
            Header: "Delivery Date",
            accessor: (row: any) => [row?.data?.schedule?.stop1_date, row?.data?.schedule?.stop1_end_date],
            minWidth: 187,
            Cell: ({ value }: any) => <div>{formatDateRange(value[0], value[1])}</div>,
          },
        ]
      : []),
    {
      id: "weight_kg",
      Header: (
        <span className="w-100 d-flex flex-end justify-content-end">
          <FormattedMessage id="general.weight" defaultMessage="Weight" />{" "}
          {unit_system_preferences === "inch-lbs" ? "(lbs)" : "(kg)"}{" "}
        </span>
      ),
      accessor: (row) => [row?.data?.organization?.good_items],
      minWidth: isShipmentReview ? 168 : 123,
      Cell: ({ value }: any) => {
        const totalWeight = value[0]?.reduce((acc: number, goodItem: IGoodItem) => {
          const toUnit = unit_system_preferences === "inch-lbs" ? "lbs" : "kg";
          const itemUnit = goodItem.w_unit === "Lbs" ? "lbs" : "kg";
          const itemWeight = (goodItem?.weight || 0) * (goodItem.units || 1);
          const convertedWeight = parseFloat(convertWeight(itemWeight, { fromUnit: itemUnit, toUnit }));
          return acc + convertedWeight;
        }, 0);
        return <div className="d-flex flex-end justify-content-end">{formatWeight(totalWeight.toFixed(2))}</div>;
      },
    },
    {
      id: "volume_m",
      Header: (
        <span className="w-100 d-flex flex-end justify-content-end">
          <FormattedMessage id="new-request.selected-orders.volume" defaultMessage="Volume" />{" "}
          <span>
            (m<sup>3</sup>)
          </span>
        </span>
      ),
      accessor: (row) => [row?.data?.organization?.good_items],
      minWidth: isShipmentReview ? 168 : 112,
      Cell: ({ value }: any) => {
        let totalCBM = 0;
        value[0]?.forEach((goodItem: IGoodItem) => {
          totalCBM += goodItem.total_cbm;
        });
        return <div className="d-flex flex-end justify-content-end">{formatWeight(totalCBM.toFixed(2))}</div>;
      },
    },
  ];

  return columns;
}

export default getSelectionOrderColumns;
